import { useState } from "react";
import type { FormEvent } from "react";
import {
  PaymentsOutlined as CollectedIcon,
  EventRepeat as UpcomingIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
// KpiRow / coachTokens are still untyped .js neighbours — allowJs resolves them (checkJs is off),
// so their exports arrive as `any`. That's fine for this gate; convert them separately later.
import { KPI } from "./coach/KpiRow";
import { t } from "./coach/coachTokens";
import type { ArboxCollectionsStats } from "../../api/generated";

// The operator's OWN Arbox money, for one membership type — a different lens from the all-companies
// platform metrics next to it, which is why it gets its own row rather than two more cells in
// CompanyOverviewKpiRow. Super-admin only: the endpoint 403s for a group admin, so the caller passes
// null and the whole row disappears.
//
// Two cards, deliberately different in kind:
//   - settled   — what was actually charged over the last COMPLETED month.
//   - scheduled — what Arbox is set to bill next month, read off the standing orders. Forward-looking
//                 but EXACT, so it is presented as a figure and not as an estimate.
//
// Both come from a nightly job, not from this request — building the scheduled figure costs one Arbox
// call per member and rate-limits if done on demand. That is why the header carries an "updated at"
// stamp: these numbers are always a few hours old and the operator has to be able to see how old.
//
// "גבייה" (collections), never "הכנסות" (revenue). The figures come from Arbox's payment-transaction
// ledger, which misses manually-recorded payments (cash, bank transfer, cheque) and so reads under
// Arbox's own Revenue page. Calling it revenue invites someone to reconcile the two and conclude the
// tiles are broken. See ArboxCollectionsStats on the server for the measurements.
type ArboxCollectionsRowProps = {
  collections?: ArboxCollectionsStats | null;
};

// Shown when `available` is false. That flag covers two situations — no finance key configured, and no
// nightly run has succeeded yet — and the UI cannot tell them apart, so the caption has to be true of
// both. It previously read "אין חיבור לארבוקס" ("no connection to Arbox"), which was accurate when the
// figures were fetched per request but is actively misleading now that they come from a stored snapshot:
// on a fresh environment the connection is fine and the table is simply still empty.
const UNCOMPUTED = "טרם חושב";

// Asked for before the blur comes off. This is a SPEED BUMP, not a secret: the app is a static bundle,
// so the string below ships to every browser that loads the dashboard and the figures themselves are
// already in the page's network response. It stops someone who grabbed an unlocked laptop from clicking
// the tile — it stops nobody who opens devtools. Real gating would have to happen server-side, by not
// sending ArboxCollectionsStats to that session at all.
const REVEAL_PASSCODE = "neri47!";

// The subline says up front that a passcode is coming, so the click doesn't feel like a dead end for
// someone who doesn't have one.
const REVEAL_PROMPT = "לחצו לחשיפה · נדרשת סיסמה";

const ArboxCollectionsRow = ({ collections }: ArboxCollectionsRowProps) => {
  // Blurred until unlocked. This dashboard gets opened in demos and on shared screens, and these are the
  // numbers nobody else in the room should read at a glance. One toggle covers both tiles — it is a
  // single disclosure, not two. Resets on mount (no persistence), so a reload re-hides and the passcode
  // is asked for again. A guard against shoulder-surfing only, NOT a security control — see the note on
  // REVEAL_PASSCODE.
  const [revealed, setRevealed] = useState(false);
  // Passcode prompt: open state, what's typed, and whether the last attempt was wrong.
  const [asking, setAsking] = useState(false);
  const [entry, setEntry] = useState("");
  const [wrong, setWrong] = useState(false);

  if (!collections) return null;

  const available = !!collections.available;
  const hidden = available && !revealed;
  const { lastMonth, upcoming } = collections;

  // Whole shekels — agorot are noise on a monthly total, and a tile has to fit a 6-figure number.
  const money = (v?: number | null): string =>
    v == null ? "—" : `₪${Math.round(Number(v)).toLocaleString()}`;
  const count = (v?: number | null): string => (v == null ? "—" : Number(v).toLocaleString());

  // "19.8, 03:30" — day and time are what matter for "is this figure from last night or last week".
  const asOfLabel = (iso?: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString("he-IL", {
          day: "numeric",
          month: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  // "אוגוסט 2026" from an ISO date, for tile captions.
  const monthLabel = (iso?: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("he-IL", { month: "long", year: "numeric" });
  };

  const blur = (text: string) =>
    hidden ? (
      <Box
        component="span"
        sx={{
          filter: "blur(11px)",
          userSelect: "none",
          display: "inline-block",
          // The blur bleeds past the glyphs; without this the halo clips at the tile edge.
          paddingInline: "6px",
        }}
      >
        {text}
      </Box>
    ) : (
      text
    );

  // Closing always clears what was typed, so a cancelled attempt leaves nothing behind for the next one.
  const closeAsk = () => {
    setAsking(false);
    setEntry("");
    setWrong(false);
  };
  // Hiding again is free — only the reveal direction costs a passcode, or re-hiding becomes something
  // an operator avoids doing when someone walks up.
  const toggle = available
    ? () => {
        if (revealed) setRevealed(false);
        else {
          setEntry("");
          setWrong(false);
          setAsking(true);
        }
      }
    : undefined;
  const submitPasscode = () => {
    if (entry === REVEAL_PASSCODE) {
      setRevealed(true);
      closeAsk();
    } else {
      setWrong(true);
    }
  };
  const aria = (what: string) => (available ? `${hidden ? "הצגת" : "הסתרת"} ${what}` : undefined);

  // While hidden the subline carries the affordance — otherwise a blurred number reads as a bug. Once
  // revealed this card has nothing more to add: the failed-charge line that used to sit here was
  // removed, and the API still carries failedAmount/failedCount for whoever wants them next.
  const settledSub = !available ? UNCOMPUTED : hidden ? REVEAL_PROMPT : undefined;

  // Names the source rather than hedging the number. An earlier revision reconstructed this from plan
  // prices and had to be labelled an estimate; it is now read off the standing orders themselves and
  // reconciles to the shekel against Arbox's own Future Transactions export.
  const upcomingSub = !available
    ? UNCOMPUTED
    : hidden
      ? REVEAL_PROMPT
      : "לפי הוראות הקבע בארבוקס";

  return (
    <Box sx={{ marginBottom: "18px" }}>
      {collections.membership && (
        <Box sx={{ fontSize: "12px", color: t.t2, fontWeight: 600, marginBottom: "8px" }}>
          גבייה — {collections.membership}
          {available && asOfLabel(collections.computedAt) && (
            <Box component="span" sx={{ color: t.t3, fontWeight: 500 }}>
              {" · "}עודכן {asOfLabel(collections.computedAt)}
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          gap: "14px",
        }}
      >
        <KPI
          icon={<CollectedIcon sx={{ fontSize: 22 }} />}
          iconBg={t.greenLight}
          iconColor={t.green}
          badge={available && lastMonth?.collectedCount != null ? `${count(lastMonth.collectedCount)} עסקאות` : null}
          badgeBg={t.greenLight}
          badgeColor={t.greenText}
          value={blur(available ? money(lastMonth?.collected) : "—")}
          valueColor={available ? t.text : t.t3}
          label={`נגבה בחודש שעבר${monthLabel(lastMonth?.fromDate) ? ` · ${monthLabel(lastMonth?.fromDate)}` : ""}`}
          sub={settledSub}
          interactive={available}
          onClick={toggle}
          ariaLabel={aria("נתוני הגבייה")}
        />
        <KPI
          icon={<UpcomingIcon sx={{ fontSize: 22 }} />}
          iconBg={t.blueLight}
          iconColor={t.blue}
          badge={available && upcoming?.scheduledCount != null ? `${count(upcoming.scheduledCount)} חיובים` : null}
          badgeBg={t.blueLight}
          badgeColor={t.blueText}
          value={blur(available ? money(upcoming?.scheduledAmount) : "—")}
          valueColor={available ? t.text : t.t3}
          label={`מתוזמן לחודש הבא${monthLabel(upcoming?.chargeMonth) ? ` · ${monthLabel(upcoming?.chargeMonth)}` : ""}`}
          sub={upcomingSub}
          interactive={available}
          onClick={toggle}
          ariaLabel={aria("הגבייה המתוזמנת")}
        />
      </Box>
      {/* A form, not two loose handlers: Enter has to submit, or the prompt feels broken to anyone who
          types a password without reaching for the mouse. */}
      <Dialog open={asking} onClose={closeAsk} maxWidth="xs" fullWidth>
        <Box
          component="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            submitPasscode();
          }}
        >
          <DialogTitle sx={{ fontSize: "16px", fontWeight: 700 }}>חשיפת נתוני הגבייה</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: "13px", marginBottom: "12px" }}>
              הזינו סיסמה כדי להציג את הסכומים.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="password"
              label="סיסמה"
              value={entry}
              onChange={(e) => {
                setEntry(e.target.value);
                setWrong(false);
              }}
              error={wrong}
              helperText={wrong ? "סיסמה שגויה" : " "}
              inputProps={{ "aria-label": "סיסמה" }}
            />
          </DialogContent>
          <DialogActions sx={{ padding: "0 24px 16px" }}>
            <Button onClick={closeAsk} sx={{ fontWeight: 600 }}>
              ביטול
            </Button>
            <Button type="submit" variant="contained" disableElevation sx={{ fontWeight: 600 }}>
              חשיפה
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ArboxCollectionsRow;
